/**
 * Une conversation ouverte (docs/13 §4, §5).
 *
 * ⚠️ **L'historique arrive antéchronologique et s'affiche dans l'autre sens.**
 * Le serveur rend la page la plus récente d'abord — c'est ce qui rend le
 * curseur utilisable — et c'est au client de retourner la liste. Le faire côté
 * serveur rendrait le curseur incompréhensible : il désignerait le premier
 * élément du tableau et non le dernier lu.
 *
 * La lecture est marquée **à l'ouverture et à chaque arrivée**, et non sur un
 * bouton : le geste que l'utilisateur fait pour lire est d'ouvrir la
 * conversation, et lui demander de confirmer qu'il a lu ferait un badge qui ne
 * redescend jamais.
 */
import { useEffect, useState } from "react";
import { useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import {
  useConversation,
  useDeleteMessage,
  useMarkConversationRead,
  useMessages,
  useReportMessage,
  useSendMessage,
  useSetConversationFlag,
} from "@/features/messaging/api/useMessaging";
import { MessageBubble } from "@/features/messaging/components/MessageBubble";
import { MessageComposer } from "@/features/messaging/components/MessageComposer";
import { useDebouncedValue } from "@/shared/lib/useDebouncedValue";
import { useSessionStore } from "@/shared/auth/session";
import { Button } from "@/shared/ui/Button";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { Field } from "@/shared/ui/Field";
import { Skeleton } from "@/shared/ui/Skeleton";

export function ConversationPage() {
  const { t } = useTranslation("messaging");
  // `/public/...` et non `/messages/...` : l'identifiant de route porte le
  // préfixe de la route « pathless » qui pose le layout, alors que l'URL, elle,
  // n'en a pas. Le typage de TanStack Router le dit — et c'est la seule raison
  // pour laquelle ce chemin ressemble à une faute.
  const { conversationId } = useParams({ from: "/public/messages/$conversationId" });
  const userId = useSessionStore((state) => state.user?.id);

  const [search, setSearch] = useState("");
  // La saisie est immédiate, la requête attend le silence : voir
  // `useDebouncedValue`. Les deux valeurs coexistent — l'une pour le champ,
  // l'autre pour le serveur — et les confondre ferait soit un champ qui traîne
  // d'un caractère, soit une requête par frappe.
  const searched = useDebouncedValue(search);
  const isSearching = searched.trim() !== "";

  const conversation = useConversation(conversationId);
  const history = useMessages(conversationId, searched);
  const send = useSendMessage(conversationId);
  const remove = useDeleteMessage();
  const report = useReportMessage();
  const markRead = useMarkConversationRead();
  const mute = useSetConversationFlag("mute");
  const archive = useSetConversationFlag("archive");

  const unread = conversation.data?.unread_count ?? 0;
  const { mutate: markConversationRead } = markRead;
  useEffect(() => {
    // Conditionné aux non-lus : sans ce garde, chaque invalidation déclencherait
    // un `POST /read`, qui invaliderait à son tour — une boucle que rien
    // n'arrête et qui ne se voit que dans l'onglet réseau.
    if (unread > 0) {
      markConversationRead(conversationId);
    }
  }, [conversationId, unread, markConversationRead]);

  const counterpart = conversation.data?.participants.find(
    (participant) => participant.user_id !== userId,
  );

  return (
    <section className="mx-auto flex max-w-3xl flex-col gap-4 px-4 py-8">
      <header className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
          {counterpart?.display_name ?? t("list.unknownParticipant")}
        </h1>
        {conversation.data !== undefined ? (
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                mute.mutate({
                  conversationId,
                  enabled: !conversation.data.is_muted,
                });
              }}
            >
              {conversation.data.is_muted ? t("actions.unmute") : t("actions.mute")}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                archive.mutate({
                  conversationId,
                  enabled: !conversation.data.is_archived,
                });
              }}
            >
              {conversation.data.is_archived ? t("actions.unarchive") : t("actions.archive")}
            </Button>
          </div>
        ) : null}
      </header>

      {conversation.isError ? <ErrorState error={conversation.error} /> : null}

      <Field
        type="search"
        label={t("search.label")}
        placeholder={t("search.placeholder")}
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
        }}
      />

      {history.isPending ? <Skeleton className="h-64 w-full" label={t("title")} /> : null}
      {history.isError ? <ErrorState error={history.error} /> : null}

      {history.data !== undefined ? (
        <Card className="flex flex-col gap-3 p-4">
          {history.data.items.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {/* Deux phrases, parce que ce sont deux situations : un fil vide
                  invite à écrire, une recherche sans résultat ne le doit pas —
                  proposer d'écrire le premier message d'une conversation qui en
                  compte trente serait incompréhensible. */}
              {isSearching ? t("search.empty", { query: searched.trim() }) : t("thread.empty")}
            </p>
          ) : (
            <>
              {/* Hors de la liste : un décompte n'est pas un message, et le
                  glisser dans le `<ul>` le ferait annoncer comme tel par un
                  lecteur d'écran. `aria-live` est ici parce que c'est le seul
                  endroit qui change quand la recherche aboutit. */}
              {isSearching ? (
                <p className="text-sm text-slate-500 dark:text-slate-400" aria-live="polite">
                  {t("search.results", { count: history.data.items.length })}
                </p>
              ) : null}
              <ul className="flex flex-col gap-3">
                {[...history.data.items].reverse().map((message) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    isMine={message.sender_id === userId}
                    onDelete={(messageId) => {
                      remove.mutate(messageId);
                    }}
                    onReport={(messageId) => {
                      report.mutate({ messageId, reason: "inappropriate" });
                    }}
                  />
                ))}
              </ul>
            </>
          )}
        </Card>
      ) : null}

      <MessageComposer
        canPost={conversation.data?.can_post ?? false}
        isSending={send.isPending}
        onSend={(body) => {
          send.mutate(body);
        }}
      />
    </section>
  );
}
